import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import Button from '../../../../components/atoms/Button';
import Input from '../../../../components/atoms/Input';
import Label from '../../../../components/atoms/Label';
import Typo from '../../../../components/atoms/Typo';
import ContentBoxWithHeader from '../../../../components/molecules/ContentBoxWithHeader';
import InputForm from '../../../../components/molecules/InputForm';
import GridDetailTemplate from '../../../../components/templates/GridDetailTemplate';
import Alert from '../../../../components/atoms/Alert';
import { ADMIN_ROLE_MANAGEMENT_PATH } from '../../../../routes/constants/urls';
import { ButtonTypeType } from '../../../../styles/theme';
import NestablePanels from '../../../../components/molecules/NestablePanels';
import {
	getRoleTemplateAll,
	useCreateRoleMutation,
} from '../../../../store/apis/role';
import {
	CreateRoleDto,
	CreateRoleItem,
	RoleMapping,
	RoleView,
} from '../../../../interface/role';
import { ToastContext } from '../../../../contexts/Toast';
import { CustomErrorType } from '../../../../store/apis/@types';
import { fullLoadingOff, fullLoadingOn } from '../../../../store/webUtil';
import InputAlert from '../../../../components/atoms/InputAlert';

const InnerContent = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 32px;
	width: 100%;
	max-width: 830px;
	margin: 0 auto;
`;

const ButtonContainer = styled.div`
	display: flex;
	gap: 8px;
`;

const backUrl = ADMIN_ROLE_MANAGEMENT_PATH;

function AdminRoleCreatePage() {
	const navigate = useNavigate();
	const setToast = useContext(ToastContext);
	const dispatch = useDispatch();

	const [isAlertVisible, setAlertVisible] = useState(false);
	const [roleName, setRoleName] = useState('');
	const [roleData, setRoleData] = useState<RoleView[]>([]);

	const [roleNameError, setRoleNameError] = useState('');

	const [createRole] = useCreateRoleMutation();

	const handleAlertCloseClick = () => {
		setAlertVisible(false);
	};

	const handleAlertOpenClick = () => {
		setAlertVisible(true);
	};

	const handleCancelClick = () => {
		navigate(-1);
	};
	const handleCreateClick = () => {
		if (roleName.length === 0) {
			setRoleNameError('권한명을 입력해주세요.');
			return;
		}

		handleAlertOpenClick();
	};
	const hanldeCreateRole = async () => {
		const roleItems: CreateRoleItem[] = [];

		roleData.forEach((item) => {
			item.item.forEach((subItem) => {
				roleItems.push({
					isRead: subItem.isRead,
					isCreate: subItem.isCreate,
					isUpdate: subItem.isUpdate,
					isDelete: subItem.isDelete,
					isSuper: subItem.isSuper,
					roleTemplateId: subItem.key,
				});
			});
		});

		const body: CreateRoleDto = {
			roleName,
			roleItems,
		};

		const result = await createRole(body);

		if ('error' in result) {
			setToast(
				'error',
				(result.error as CustomErrorType).data.translate ||
					(result.error as CustomErrorType).data.message,
			);
		} else {
			setToast('info', '권한 등록이 완료되었습니다.');
			navigate(backUrl);
		}

		setAlertVisible(false);
	};

	const getData = useCallback(async () => {
		dispatch(fullLoadingOn());

		try {
			const result = await getRoleTemplateAll();

			const mappingRole = result.map((item) => ({
				key: item.id,
				roleCategory: item.roleCategory,
				title: item.roleName,
				isCreate: false,
				isRead: false,
				isUpdate: false,
				isDelete: false,
				isSuper: false,
			}));

			const roleItem: Record<string, RoleMapping[]> = {};

			mappingRole.forEach((item) => {
				if (roleItem[item.roleCategory]) {
					roleItem[item.roleCategory].push(item);
				} else {
					roleItem[item.roleCategory] = [item];
				}
			});

			const roleDataResult = Object.keys(roleItem).map((key, index) => ({
				key: String(index),
				title: key,
				item: roleItem[key],
			}));

			setRoleData(roleDataResult);
		} catch (e: any) {
			setToast(
				'error',
				(e.response as CustomErrorType).data.translate ||
					(e.response as CustomErrorType).data.message,
			);
		} finally {
			dispatch(fullLoadingOff());
		}
	}, [dispatch, setToast]);

	useEffect(() => {
		getData();
	}, [getData]);

	return (
		<>
			<GridDetailTemplate
				detailTitle="권한 등록"
				onBack={handleCancelClick}
				rightAccessory={
					<ButtonContainer>
						<Button
							$buttonType={ButtonTypeType.OPAQUE}
							onClick={handleCancelClick}
							size="small"
						>
							취소
						</Button>
						<Button onClick={handleCreateClick} size="small">
							등록하기
						</Button>
					</ButtonContainer>
				}
			>
				<ContentBoxWithHeader
					title="권한 관리"
					borderRadius="10px"
					className="inner-content"
				>
					<InnerContent>
						<InputForm>
							<Label name="roleName" essential>
								<span className="label">
									<Typo $typoType="label2">권한명</Typo>
								</span>
							</Label>
							<Input
								name="roleName"
								placeholder="권한명을 입력해 주세요"
								type="text"
								$inputSize="small"
								$inputRadius="small"
								value={roleName}
								onChange={(e) => {
									setRoleNameError('');
									setRoleName(e.target.value);
								}}
								onReset={() => setRoleName('')}
							/>
							{roleNameError && (
								<InputAlert $inputStatus="error">{roleNameError}</InputAlert>
							)}
						</InputForm>
						<NestablePanels
							onCrudToggle={(toggleItem) => {
								setRoleData((prev) => {
									const cloneItem = [...prev];

									const targetParent = cloneItem.find(
										(item) => item.key === toggleItem.panelParentKey,
									);

									targetParent?.item.forEach((item: any) => {
										if (item.key === toggleItem.panelItemKey) {
											switch (toggleItem.type) {
												case 'read':
													// eslint-disable-next-line no-param-reassign
													item.isRead = !item.isRead;
													break;
												case 'create':
													// eslint-disable-next-line no-param-reassign
													item.isCreate = !item.isCreate;
													break;
												case 'update':
													// eslint-disable-next-line no-param-reassign
													item.isUpdate = !item.isUpdate;
													break;
												case 'delete':
													// eslint-disable-next-line no-param-reassign
													item.isDelete = !item.isDelete;
													break;
												case 'super':
													// eslint-disable-next-line no-param-reassign
													item.isSuper = !item.isSuper;
													break;
												default:
													break;
											}
										}
									});

									return cloneItem;
								});
							}}
							data={roleData as any}
						/>
					</InnerContent>
				</ContentBoxWithHeader>
			</GridDetailTemplate>
			{isAlertVisible && (
				<Alert
					title="관리자 권한 등록"
					closeButtonClick={handleAlertCloseClick}
					onConfirmButtonText="등록하기"
					onConfirmButtonClick={hanldeCreateRole}
					isVisible={isAlertVisible}
				>
					관리자 권한을 등록하시겠습니까?
				</Alert>
			)}
		</>
	);
}

export default AdminRoleCreatePage;
